import React, { useState, useEffect, useRef } from "react";
import { Modal, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { ProdutoOportunidade } from "../../../../types/orcamento";
import { apiService } from "../../../../services/api";
import { Fornecedor } from "../../../../types/fornecedor";
import { formatCurrencyBRL } from "../../../../utils/formatters";
import { calcularMargem, nivelDaMargem } from "../../../../utils/margem";

interface ModalEditarProdutoProps {
    show: boolean;
    onHide: () => void;
    produto: ProdutoOportunidade;
    fornecedores: Fornecedor[];
    configuracao: {
        permite_editar_fornecedor: boolean;
        permite_editar_valor_orcado: boolean;
        permite_editar_valor_proposta: boolean;
    };
    onSave: () => void;
    onValueChange?: (produtoId: number, novoValorProposta: number | null) => void; // NOVA PROP
}

const ModalEditarProduto: React.FC<ModalEditarProdutoProps> = ({
    show,
    onHide,
    produto,
    fornecedores,
    configuracao,
    onSave,
    onValueChange // NOVA PROP
}) => {
    const [formData, setFormData] = useState({
        quantidade: produto.quantidade,
        valor_referencia: produto.valor_referencia,
        fornecedor_id: produto.fornecedor_id || "",
        valor_orcado: produto.valor_orcado || "",
        valor_proposta: produto.valor_proposta || "",
        numero_edital: produto.numero_edital || "",
        custo_logistico: produto.custo_logistico || 0,
        custos_adicionais: produto.custos_adicionais || 0,
        percentual_imposto: produto.percentual_imposto || 0,
        marca: (produto as any).marca || "",
        descricao_especifica: (produto as any).descricao_especifica || ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estados para cálculo da margem
    const [resultado, setResultado] = useState(0);
    const [margemPorcentagem, setMargemPorcentagem] = useState(0);
    // Falso quando faltam referência ou orçado (nada para comparar)
    const [margemCalculavel, setMargemCalculavel] = useState(false);
    // Qual valor está servindo de custo na margem (a referência é sempre a base)
    const [baseMargem, setBaseMargem] = useState<'proposta' | 'orcado'>('orcado');
    const [corFeedback, setCorFeedback] = useState('text-gray-500');
    const [mensagemFeedback, setMensagemFeedback] = useState('');

    // Estado para mostrar/esconder a seção de cálculo - sempre true quando permitido
    const [mostrarSecaoCalculo, setMostrarSecaoCalculo] = useState(true);

    // Estado para controlar a pesquisa no select de fornecedores
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Estados para validação do imposto
    const [impostoInvalido, setImpostoInvalido] = useState(false);
    const [mostrarAlertaImposto, setMostrarAlertaImposto] = useState(false);

    // Lista filtrada de fornecedores baseada no termo de pesquisa
    const filteredFornecedores = fornecedores.filter(fornecedor => {
        const searchTermLower = searchTerm.toLowerCase();

        // Busca em nome, razão social e nome fantasia
        const nomeMatch = fornecedor.nome.toLowerCase().includes(searchTermLower);
        const razaoSocialMatch = fornecedor.razao_social?.toLowerCase().includes(searchTermLower) || false;
        const nomeFantasiaMatch = fornecedor.nome_fantasia?.toLowerCase().includes(searchTermLower) || false;

        return nomeMatch || razaoSocialMatch || nomeFantasiaMatch;
    });

    // Função para validar se o imposto é obrigatório
    const validarImposto = (): boolean => {
        // Só validar se estiver editando valor de proposta E se valor de proposta foi preenchido
        const valorPropostaNum = typeof formData.valor_proposta === 'string'
            ? parseFloat(formData.valor_proposta) || 0
            : formData.valor_proposta || 0;
        const temValorProposta = valorPropostaNum > 0;

        if (!configuracao.permite_editar_valor_proposta || !temValorProposta) {
            setImpostoInvalido(false);
            setMostrarAlertaImposto(false);
            return true;
        }

        if (formData.percentual_imposto <= 0) {
            setImpostoInvalido(true);
            setMostrarAlertaImposto(true);
            return false;
        }
        setImpostoInvalido(false);
        setMostrarAlertaImposto(false);
        return true;
    };

    // Atualizar form data quando o produto muda
    useEffect(() => {
        const newFormData = {
            quantidade: produto.quantidade,
            valor_referencia: produto.valor_referencia,
            fornecedor_id: produto.fornecedor_id || "",
            valor_orcado: produto.valor_orcado || "",
            valor_proposta: produto.valor_proposta || "",
            numero_edital: produto.numero_edital || "",
            custo_logistico: produto.custo_logistico || 0,
            custos_adicionais: produto.custos_adicionais || 0,
            percentual_imposto: produto.percentual_imposto || 0,
            marca: (produto as any).marca || "",
            descricao_especifica: (produto as any).descricao_especifica || ""
        };

        setFormData(newFormData);

        // Validar imposto inicial se estiver editando valor de proposta E tiver valor de proposta
        const valorPropostaNum = typeof newFormData.valor_proposta === 'string'
            ? parseFloat(newFormData.valor_proposta) || 0
            : newFormData.valor_proposta || 0;
        const temValorProposta = valorPropostaNum > 0;

        if (configuracao.permite_editar_valor_proposta && temValorProposta) {
            const impostoValue = produto.percentual_imposto || 0;
            if (impostoValue <= 0) {
                setImpostoInvalido(true);
                setMostrarAlertaImposto(true);
            } else {
                setImpostoInvalido(false);
                setMostrarAlertaImposto(false);
            }
        } else {
            // Se não tem valor de proposta ou não pode editar, não validar imposto
            setImpostoInvalido(false);
            setMostrarAlertaImposto(false);
        }
    }, [produto, configuracao.permite_editar_valor_proposta]);

    // Adicionar listener para fechar a pesquisa quando clicar fora
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsSearchOpen(false);
            }
        }

        // Adicionar o event listener quando o componente montar
        document.addEventListener("mousedown", handleClickOutside);

        // Limpar o event listener quando o componente desmontar
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Calcular margem quando valores relevantes mudam
    useEffect(() => {
        calcularMargemModal();
    }, [
        formData.valor_proposta,
        formData.valor_referencia,
        formData.valor_orcado,
        formData.custo_logistico,
        formData.custos_adicionais,
        formData.percentual_imposto
    ]);

    // Função para formatar valores monetários (4 casas decimais)
    const formatCurrency = (value: number): string => formatCurrencyBRL(value);

    // Função para formatar porcentagem
    const formatarPorcentagem = (valor: number): string => {
        return valor.toFixed(2) + '%';
    };

    // Função para calcular a margem de lucro.
    // Base: valor de REFERÊNCIA (sempre). Custo = PROPOSTA (prevalece) ou, sem ela, o ORÇADO.
    // Fórmula única em utils/margem.ts.
    const calcularMargemModal = (): void => {
        const calculo = calcularMargem({
            valorProposta: Number(formData.valor_proposta),
            valorReferencia: Number(formData.valor_referencia),
            valorOrcado: Number(formData.valor_orcado),
            custoLogistico: Number(formData.custo_logistico),
            custosAdicionais: Number(formData.custos_adicionais),
            percentualImposto: Number(formData.percentual_imposto)
        });

        setMargemCalculavel(calculo.calculavel);
        setBaseMargem(calculo.baseUsada);

        // Sem referência ou sem proposta/orçado não há o que comparar
        if (!calculo.calculavel) {
            setResultado(0);
            setMargemPorcentagem(0);
            setCorFeedback('text-gray-500');
            setMensagemFeedback('');
            return;
        }

        setResultado(calculo.resultado);
        setMargemPorcentagem(calculo.margemPorcentagem);

        const nivel = nivelDaMargem(calculo.margemPorcentagem);
        if (nivel === 'baixa') {
            setCorFeedback('text-danger');
            setMensagemFeedback('Atenção: Margem muito baixa!');
        } else if (nivel === 'mediana') {
            setCorFeedback('text-warning');
            setMensagemFeedback('Aviso: Margem mediana');
        } else {
            setCorFeedback('text-success');
            setMensagemFeedback('Excelente: Boa margem!');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Para campos numéricos, converter para número ou string vazia
        if (['quantidade', 'valor_referencia', 'valor_orcado', 'valor_proposta', 'custo_logistico', 'custos_adicionais', 'percentual_imposto'].includes(name)) {
            const numericValue = value === "" ? "" : parseFloat(value);
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));

            // Validar imposto em tempo real se for o campo de imposto
            if (name === 'percentual_imposto' && configuracao.permite_editar_valor_proposta) {
                const valorImposto = parseFloat(value) || 0;
                // Só validar se tiver valor de proposta preenchido
                const valorPropostaNum = typeof formData.valor_proposta === 'string'
                    ? parseFloat(formData.valor_proposta) || 0
                    : formData.valor_proposta || 0;
                const temValorProposta = valorPropostaNum > 0;

                if (temValorProposta) {
                    if (valorImposto <= 0) {
                        setImpostoInvalido(true);
                        setMostrarAlertaImposto(true);
                    } else {
                        setImpostoInvalido(false);
                        setMostrarAlertaImposto(false);
                    }
                } else {
                    // Se não tem valor de proposta, não validar imposto
                    setImpostoInvalido(false);
                    setMostrarAlertaImposto(false);
                }
            }

            // Validar imposto quando o valor de proposta muda
            if (name === 'valor_proposta' && configuracao.permite_editar_valor_proposta) {
                const valorProposta = parseFloat(value) || 0;
                if (valorProposta > 0) {
                    // Agora tem valor de proposta, validar se imposto está preenchido
                    if (formData.percentual_imposto <= 0) {
                        setImpostoInvalido(true);
                        setMostrarAlertaImposto(true);
                    } else {
                        setImpostoInvalido(false);
                        setMostrarAlertaImposto(false);
                    }
                } else {
                    // Sem valor de proposta, não validar imposto
                    setImpostoInvalido(false);
                    setMostrarAlertaImposto(false);
                }
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSelectFornecedor = (fornecedor: Fornecedor) => {
        setFormData(prev => ({
            ...prev,
            fornecedor_id: fornecedor.id.toString()
        }));
        setIsSearchOpen(false);
    };

    const getFornecedorNome = (id: string | number) => {
        const idString = id.toString();
        const fornecedor = fornecedores.find(f => f.id.toString() === idString);
        if (!fornecedor) return "";

        // Lógica para determinar qual nome mostrar
        if (fornecedor.tipo_entidade === 'PJ') {
            return `${fornecedor.razao_social}${fornecedor.nome_fantasia ? ` (${fornecedor.nome_fantasia})` : ''}`;
        } else {
            return fornecedor.nome;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validar imposto antes de submeter se estiver editando valor de proposta E tiver valor de proposta preenchido
        const valorPropostaNum = typeof formData.valor_proposta === 'string'
            ? parseFloat(formData.valor_proposta) || 0
            : formData.valor_proposta || 0;
        const temValorProposta = valorPropostaNum > 0;

        if (configuracao.permite_editar_valor_proposta && temValorProposta && !validarImposto()) {
            toast.error("O percentual de imposto é obrigatório quando há valor de proposta!");
            return;
        }

        try {
            setIsSubmitting(true);

            // Preparar dados para atualização
            const updateData: any = {};

            // Sempre pode editar quantidade e valor de referência
            updateData.quantidade = formData.quantidade;
            updateData.valor_referencia = formData.valor_referencia;
            updateData.numero_edital = formData.numero_edital;

            // Sempre pode editar marca e descrição específica
            updateData.marca = formData.marca;
            updateData.descricao_especifica = formData.descricao_especifica;

            // Adicionar fornecedor se permitido
            if (configuracao.permite_editar_fornecedor && formData.fornecedor_id) {
                updateData.fornecedor_id = parseInt(formData.fornecedor_id.toString());
            }

            // Adicionar valor orçado se permitido
            if (configuracao.permite_editar_valor_orcado && formData.valor_orcado !== "") {
                updateData.valor_orcado = formData.valor_orcado;
            }

            // Adicionar valor proposta e campos de cálculo se permitido
            if (configuracao.permite_editar_valor_proposta && formData.valor_proposta !== "") {
                updateData.valor_proposta = formData.valor_proposta;
                updateData.custo_logistico = formData.custo_logistico;
                updateData.custos_adicionais = formData.custos_adicionais;
                updateData.percentual_imposto = formData.percentual_imposto;
                // A margem será calculada automaticamente no backend
            }

            // Enviar requisição
            await apiService.updateProdutoOportunidade(produto.id, updateData);

            toast.success("Produto atualizado com sucesso!");

            // NOVA FUNCIONALIDADE: Notificar sobre mudança de valor
            if (configuracao.permite_editar_valor_proposta && onValueChange) {
                const novoValorProposta = formData.valor_proposta !== "" ? Number(formData.valor_proposta) : null;
                onValueChange(produto.id, novoValorProposta);
            }

            onSave();
        } catch (error) {
            console.error("Erro ao atualizar produto:", error);
            toast.error("Erro ao atualizar produto");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Calcular subtotal com base na quantidade e valor referência
    const subtotal = formData.quantidade * formData.valor_referencia;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            className="custom-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>Editar Produto</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    {/* Alerta para imposto obrigatório */}
                    {mostrarAlertaImposto && configuracao.permite_editar_valor_proposta && (
                        <Alert variant="warning" className="mb-3">
                            <i className="ti ti-alert-triangle me-2"></i>
                            <strong>Atenção!</strong> O percentual de imposto é obrigatório e deve ser maior que zero.
                        </Alert>
                    )}

                    {/* Informações do produto */}
                    <div className="mb-4">
                        <h5 className="fw-bold">{produto.nome_produto}</h5>
                        <p className="text-muted">Unidade de medida: {produto.unidade_medida}</p>
                    </div>

                    {/* Campos de Marca e Descrição Específica */}
                    <div className="card mb-3 border">
                        <div className="card-header">
                            <h6 className="mb-0">Especificações do Produto</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {/* Marca */}
                                <div className="col-md-6 mb-3">
                                    <label className="col-form-label">
                                        Marca
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="marca"
                                        value={formData.marca}
                                        onChange={handleChange}
                                        placeholder="Ex: Moinho Fino, Tio João, Camil..."
                                    />
                                    <small className="text-muted">Marca específica para esta oportunidade</small>
                                </div>

                                {/* Descrição Específica */}
                                <div className="col-md-6 mb-3">
                                    <label className="col-form-label">
                                        Descrição Específica
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="descricao_especifica"
                                        value={formData.descricao_especifica}
                                        onChange={handleChange}
                                        rows={3}
                                        placeholder="Ex: Tipo A Premium, torra média, embalagem à vácuo..."
                                    />
                                    <small className="text-muted">Descrição específica para esta oportunidade</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Número do Edital */}
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Número no Edital
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                name="numero_edital"
                                value={formData.numero_edital}
                                onChange={handleChange}
                                placeholder="Ex: Item 1.2 / Lote A3"
                            />
                        </div>

                        {/* Quantidade */}
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Quantidade <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                name="quantidade"
                                value={formData.quantidade}
                                onChange={handleChange}
                                min="0.01"
                                step="0.01"
                                required
                            />
                        </div>

                        {/* Valor de Referência */}
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Valor de Referência <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">R$</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="valor_referencia"
                                    value={formData.valor_referencia}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.0001"
                                    required
                                />
                            </div>
                        </div>

                        {/* Subtotal de Referência - Não editável */}
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Subtotal
                            </label>
                            <div className="form-control bg-light">
                                {formatCurrency(subtotal)}
                            </div>
                        </div>
                    </div>

                    {/* Seção de Fornecedor e Valor Orçado */}
                    <div className="card mb-3 border">
                        <div className="card-header">
                            <h6 className="mb-0">Fornecedor e Valor Orçado</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {/* Fornecedor com pesquisa */}
                                <div className="col-md-6 mb-3">
                                    <label className="col-form-label">
                                        Fornecedor
                                        {!configuracao.permite_editar_fornecedor &&
                                            <span className="text-muted ms-1">(Bloqueado nesta etapa)</span>}
                                    </label>
                                    <div className="position-relative" ref={searchRef}>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Pesquisar fornecedor..."
                                                value={formData.fornecedor_id ? getFornecedorNome(formData.fornecedor_id) : searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setFormData(prev => ({ ...prev, fornecedor_id: "" }));
                                                    setIsSearchOpen(true);
                                                }}
                                                onClick={() => setIsSearchOpen(true)}
                                                disabled={!configuracao.permite_editar_fornecedor}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                    setSearchTerm("");
                                                    setFormData(prev => ({ ...prev, fornecedor_id: "" }));
                                                }}
                                                disabled={!configuracao.permite_editar_fornecedor}
                                            >
                                                <i className="ti ti-x"></i>
                                            </button>
                                        </div>

                                        {isSearchOpen && configuracao.permite_editar_fornecedor && (
                                            <div className="position-absolute start-0 w-100 mt-1 bg-white shadow-sm border rounded z-index-dropdown" style={{ maxHeight: "200px", overflowY: "auto", zIndex: 1000 }}>
                                                {filteredFornecedores.length === 0 ? (
                                                    <div className="p-2 text-muted">Nenhum fornecedor encontrado</div>
                                                ) : (
                                                    filteredFornecedores.map(fornecedor => (
                                                        <div
                                                            key={fornecedor.id}
                                                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                                                            onClick={() => handleSelectFornecedor(fornecedor)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {fornecedor.tipo_entidade === 'PJ' ? (
                                                                <>
                                                                    <div><strong>{fornecedor.razao_social}</strong></div>
                                                                    {fornecedor.nome_fantasia && <small className="text-muted">{fornecedor.nome_fantasia}</small>}
                                                                </>
                                                            ) : (
                                                                <div><strong>{fornecedor.nome}</strong></div>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Valor Orçado */}
                                <div className="col-md-6 mb-3">
                                    <label className="col-form-label">
                                        Valor Orçado
                                        {!configuracao.permite_editar_valor_orcado &&
                                            <span className="text-muted ms-1">(Bloqueado nesta etapa)</span>}
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">R$</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="valor_orcado"
                                            value={formData.valor_orcado}
                                            onChange={handleChange}
                                            min="0.0001"
                                            step="0.0001"
                                            disabled={!configuracao.permite_editar_valor_orcado}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Seção de Valor de Proposta com Calculadora de Margem */}
                    <div className="card mb-3 border">
                        <div className="card-header bg-primary-light d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Valor de Proposta e Margem</h6>
                            {configuracao.permite_editar_valor_proposta && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-primary"
                                    onClick={() => setMostrarSecaoCalculo(!mostrarSecaoCalculo)}
                                >
                                    {mostrarSecaoCalculo ? (
                                        <><i className="ti ti-chevron-up me-1"></i>Esconder Calculadora</>
                                    ) : (
                                        <><i className="ti ti-calculator me-1"></i>Mostrar Calculadora</>
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {/* Valor de Proposta */}
                                <div className="col-md-8 mb-3">
                                    <label className="col-form-label">
                                        Valor de Proposta
                                        {!configuracao.permite_editar_valor_proposta &&
                                            <span className="text-muted ms-1">(Bloqueado nesta etapa)</span>}
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">R$</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="valor_proposta"
                                            value={formData.valor_proposta}
                                            onChange={handleChange}
                                            min="0.0001"
                                            step="0.0001"
                                            disabled={!configuracao.permite_editar_valor_proposta}
                                        />
                                    </div>
                                </div>

                                {/* Exibir Margem de Lucro como Badge */}
                                <div className="col-md-4 mb-3">
                                    <label className="col-form-label">Margem de Lucro</label>
                                    <div className="form-control bg-light d-flex align-items-center justify-content-center">
                                        {margemCalculavel ? (
                                            <span className={`badge ${corFeedback.replace('text-', 'bg-')} rounded-pill fs-6`}>
                                                {formatarPorcentagem(margemPorcentagem)}
                                            </span>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        {baseMargem === 'proposta'
                                            ? 'Valor de referência x valor de proposta'
                                            : 'Valor de referência x valor orçado (sem proposta)'}
                                    </small>
                                    {mensagemFeedback && (
                                        <small className={`${corFeedback} d-block mt-1`}>
                                            {mensagemFeedback}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Seção de Cálculo da Margem - Sempre visível quando permitido */}
                            {configuracao.permite_editar_valor_proposta && (
                                <>
                                    <hr />
                                    <div className="row mb-2">
                                        <div className="col-12">
                                            <h6 className="mb-3">Calculadora de Margem</h6>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Custo Logístico</label>
                                            <div className="input-group">
                                                <span className="input-group-text">R$</span>
                                                <input
                                                    type="number"
                                                    step="0.0001"
                                                    value={formData.custo_logistico}
                                                    onChange={(e) => handleChange({
                                                        target: {
                                                            name: 'custo_logistico',
                                                            value: e.target.value
                                                        }
                                                    } as React.ChangeEvent<HTMLInputElement>)}
                                                    className="form-control"
                                                    min="0"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Custos Adicionais</label>
                                            <div className="input-group">
                                                <span className="input-group-text">R$</span>
                                                <input
                                                    type="number"
                                                    step="0.0001"
                                                    value={formData.custos_adicionais}
                                                    onChange={(e) => handleChange({
                                                        target: {
                                                            name: 'custos_adicionais',
                                                            value: e.target.value
                                                        }
                                                    } as React.ChangeEvent<HTMLInputElement>)}
                                                    className="form-control"
                                                    min="0"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">
                                                Imposto {(() => {
                                                    const valorPropostaNum = typeof formData.valor_proposta === 'string'
                                                        ? parseFloat(formData.valor_proposta) || 0
                                                        : formData.valor_proposta || 0;
                                                    return valorPropostaNum > 0;
                                                })() && <span className="text-danger">*</span>}
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.percentual_imposto}
                                                    onChange={(e) => handleChange({
                                                        target: {
                                                            name: 'percentual_imposto',
                                                            value: e.target.value
                                                        }
                                                    } as React.ChangeEvent<HTMLInputElement>)}
                                                    className={`form-control ${impostoInvalido ? 'is-invalid' : ''}`}
                                                    max="100"
                                                    required={(() => {
                                                        const valorPropostaNum = typeof formData.valor_proposta === 'string'
                                                            ? parseFloat(formData.valor_proposta) || 0
                                                            : formData.valor_proposta || 0;
                                                        return valorPropostaNum > 0;
                                                    })()}
                                                />
                                                <span className="input-group-text">%</span>
                                                {impostoInvalido && (
                                                    <div className="invalid-feedback">
                                                        O percentual de imposto é obrigatório quando há valor de proposta
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Resultado (Lucro)</label>
                                            <div className="form-control bg-light text-center fw-bold">
                                                {formatCurrency(resultado)}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Lucro Total (x{formData.quantidade})</label>
                                            <div className="form-control bg-light text-center fw-bold">
                                                {formatCurrency(resultado * formData.quantidade)}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="col-lg-12 text-end modal-btn mt-4">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={onHide}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting || (configuracao.permite_editar_valor_proposta && (() => {
                                const valorPropostaNum = typeof formData.valor_proposta === 'string'
                                    ? parseFloat(formData.valor_proposta) || 0
                                    : formData.valor_proposta || 0;
                                return valorPropostaNum > 0;
                            })() && impostoInvalido)}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ModalEditarProduto;